import React, { useState, useEffect, useRef } from 'react';
import {
  User, Mail, Phone, MapPin, Calendar, Briefcase,
  Camera, Save, Edit, X, Shield, Building, Star,
  CheckCircle, AlertCircle, Upload, GraduationCap,
  FileText, Award, Plus, Trash2, Eye, Download, Lock,
  Heart, Users, Baby, BookOpen, Clock, Key, ChevronRight,
  Layers, AlertTriangle, Info
} from 'lucide-react';

/* ─── Paleta ProTalentHub / ProDominicana ─────────────────────── */
const C = {
  navy:    '#0f2d6e',
  blue:    '#1d4ed8',
  sky:     '#0ea5e9',
  gold:    '#f59e0b',
  green:   '#059669',
  red:     '#dc2626',
  gray50:  '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray400: '#94a3b8',
  gray600: '#475569',
  gray800: '#1e293b',
  white:   '#ffffff',
};

const API_URL  = process.env.REACT_APP_API_URL;
const getToken = () => localStorage.getItem('dths_token');

const api = async (path, opts = {}) => {
  const tok = localStorage.getItem('dths_token') || sessionStorage.getItem('dths_token');
  const { headers: optsHeaders, ...restOpts } = opts;
  const res = await fetch(`${API_URL}${path}`, {
    ...restOpts,
    headers: { Authorization: `Bearer ${tok || getToken()}`, 'Content-Type': 'application/json', ...optsHeaders },
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
  return res.json();
};

/* ─── Helpers ─────────────────────────────────────────────────── */
const calcAntiguedad = (fecha) => {
  if (!fecha) return { label: 'N/D', años: 0, meses: 0 };
  const d = new Date(fecha), h = new Date();
  const ms = h - d, años = Math.floor(ms / (365.25 * 864e5));
  const meses = Math.floor((ms % (365.25 * 864e5)) / (30.44 * 864e5));
  return { label: `${años}a ${meses}m`, años, meses };
};

const calcEdad = (fecha) => {
  if (!fecha) return null;
  const d = new Date(fecha), h = new Date();
  return Math.floor((h - d) / (365.25 * 864e5));
};

const fmtFecha = (f) => {
  if (!f) return '—';
  const [year, month, day] = f.split('T')[0].split('-');
  return new Date(year, month - 1, day).toLocaleDateString('es-DO', { day:'2-digit', month:'short', year:'numeric' });
};

/* ─── Toast ───────────────────────────────────────────────────── */
const Toast = ({ tipo, texto, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const ok = tipo === 'success';
  return (
    <div style={{
      position:'fixed', top:24, right:24, zIndex:9999,
      background: ok ? '#ecfdf5' : '#fef2f2',
      border: `1px solid ${ok ? '#6ee7b7' : '#fca5a5'}`,
      color: ok ? '#065f46' : '#991b1b',
      borderRadius:12, padding:'14px 20px',
      display:'flex', alignItems:'center', gap:10,
      boxShadow:'0 10px 25px rgba(0,0,0,.12)',
      maxWidth:380, animation:'slideIn .3s ease',
    }}>
      {ok ? <CheckCircle size={18}/> : <AlertCircle size={18}/>}
      <span style={{fontWeight:500, fontSize:14}}>{texto}</span>
      <button onClick={onClose} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'inherit'}}><X size={16}/></button>
    </div>
  );
};

/* ─── Campo con candado ───────────────────────────────────────── */
const Field = ({ label, value, editable = false, onChange, type = 'text', placeholder, fullWidth, rows }) => {
  const inputStyle = {
    width:'100%', padding:'10px 12px', borderRadius:8, fontSize:14,
    border:`1px solid ${editable ? C.gray200 : C.gray200}`,
    background: editable ? C.white : C.gray100,
    color: editable ? C.gray800 : C.gray600,
    boxSizing:'border-box', outline:'none', fontFamily:'inherit',
    resize: rows ? 'vertical' : undefined,
    transition:'border .2s, box-shadow .2s',
  };

  return (
    <div style={{ gridColumn: fullWidth ? '1/-1' : undefined }}>
      <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:6}}>
        <label style={{fontSize:12, fontWeight:600, color: C.gray600, textTransform:'uppercase', letterSpacing:.5}}>{label}</label>
        {!editable && <Lock size={11} color={C.gray400}/>}
      </div>
      {rows ? (
        <textarea rows={rows} value={value || ''} onChange={onChange} disabled={!editable}
          style={inputStyle} placeholder={editable ? placeholder : undefined}/>
      ) : (
        <input type={type} value={value || ''} onChange={onChange} disabled={!editable}
          style={inputStyle} placeholder={editable ? placeholder : undefined}/>
      )}
    </div>
  );
};

/* ─── Section card ─────────────────────────────────────────────── */
const Card = ({ title, icon: Icon, children, action }) => (
  <div style={{background:C.white, borderRadius:12, border:`1px solid ${C.gray200}`, overflow:'hidden', marginBottom:20}}>
    <div style={{
      display:'flex', justifyContent:'space-between', alignItems:'center',
      padding:'16px 20px', background: C.gray50, borderBottom:`1px solid ${C.gray200}`,
    }}>
      <div style={{display:'flex', alignItems:'center', gap:8}}>
        {Icon && <div style={{width:28, height:28, borderRadius:6, background:`${C.blue}18`, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <Icon size={15} color={C.blue}/>
        </div>}
        <span style={{fontSize:14, fontWeight:700, color:C.gray800}}>{title}</span>
      </div>
      {action}
    </div>
    <div style={{padding:'20px'}}>{children}</div>
  </div>
);

/* ─── MAIN COMPONENT ──────────────────────────────────────────── */
const PerfilModule = () => {
  const [tab, setTab]               = useState('personal');
  const [perfil, setPerfil]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [editPersonal, setEditP]    = useState(false);
  const [toast, setToast]           = useState(null);
  const [stats, setStats]           = useState({ capacitacionesCurso:0, capacitacionesPendientes:0 });

  /* sub-data */
  const [ficha, setFicha]           = useState(null);
  const [hijos, setHijos]           = useState([]);
  const [contactosEmerg, setContactosEmerg]   = useState([]);
  const [historial, setHistorial]   = useState([]);
  const [docs, setDocs]             = useState([]);
  const [dragOver, setDragOver]     = useState(false);
  const fileRef                     = useRef();
  const fotoRef                     = useRef();

  /* edit state */
  const [formP, setFormP] = useState({ telefono2:'', telefono3:'', emailPersonal:'', direccion2:'', direccion3:'' });
  const [formF, setFormF] = useState({ fechaNacimiento:'', sexo:'', direccionLaboral1:'' });
  const [editFicha, setEditFicha]   = useState(false);
  const [formHijo, setFormHijo]     = useState({ nombre:'', sexo:'', fechaNacimiento:'' });
  const [showHijoForm, setShowHijo] = useState(false);
  const [formE, setFormE]           = useState({ nombreContacto:'', parentesco:'', telefonoContacto:'' });
  const [editEmerg, setEditEmerg]   = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [pw, setPw]                 = useState({ actual:'', nuevo:'', confirmar:'' });

  /* preview foto */
  const [previewFoto, setPreviewFoto] = useState(null);
  const [fotoFile, setFotoFile]       = useState(null);

  const msg = (tipo, texto) => setToast({ tipo, texto });

  /* ── Carga inicial ── */
  useEffect(() => { if (getToken()) bootstrap(); }, []);

  const bootstrap = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPerfil(), loadFicha(), loadContactoEmerg(),
        loadHistorial(), loadDocs(), loadStats(),
      ]);
    } finally { setLoading(false); }
  };

  const loadPerfil = async () => {
    const d = await api('/perfil');
    setPerfil(d.perfil);
    setFormP({
      telefono2: d.perfil.telefono2 || '',
      telefono3: d.perfil.telefono3 || '',
      emailPersonal: d.perfil.emailPersonal || '',
      direccion2: d.perfil.direccion2 || '',
      direccion3: d.perfil.direccion3 || '',
    });
  };

  const loadFicha = async () => {
    try {
      const d = await api('/perfil/ficha');
      setFicha(d.ficha); setHijos(d.hijos || []);
      setFormF({ fechaNacimiento: d.ficha?.fechaNacimiento?.split('T')[0] || '', sexo: d.ficha?.sexo || '' });
    } catch { setFicha({}); }
  };

  const loadContactoEmerg = async () => {
    if (!getToken()) return;
    try {
      const d = await api('/perfil/contacto-emergencia');
      setContactosEmerg(d.contactos || (d.contacto ? [d.contacto] : []));
      setFormE({ nombreContacto: d.contacto?.nombreContacto || '', parentesco: d.contacto?.parentesco || '', telefonoContacto: d.contacto?.telefonoContacto || '' });
    } catch { setContactosEmerg([]); }
  };

  const loadHistorial = async () => {
    try { const d = await api('/perfil/historial-laboral'); setHistorial(d.historial || []); } catch { setHistorial([]); }
  };

  const loadDocs = async () => {
    try { const d = await api('/perfil/documentos'); setDocs(d.documentos || []); } catch { setDocs([]); }
  };

  const loadStats = async () => {
    try { const d = await api('/perfil/estadisticas'); setStats(d.estadisticas || {}); } catch {}
  };

  /* ── Guardar perfil personal ── */
  const savePersonal = async () => {
    setSaving(true);
    try {
      await api('/perfil', { method:'PUT', body: JSON.stringify(formP) });
      await loadPerfil();
      setEditP(false);
      msg('success', '¡Información de contacto actualizada!');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  /* ── Foto ── */
  const handleFotoSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { msg('error', 'La imagen no debe superar 5MB'); return; }
    setFotoFile(f);
    const r = new FileReader(); r.onloadend = () => setPreviewFoto(r.result); r.readAsDataURL(f);
  };

  const uploadFoto = async () => {
    if (!fotoFile) return;
    setSaving(true);
    try {
      const fd = new FormData(); fd.append('foto', fotoFile);
      const res = await fetch(`${API_URL}/perfil/foto`, {
        method:'POST', headers:{ Authorization:`Bearer ${getToken()}` }, body:fd,
      });
      if (!res.ok) throw new Error('Error al subir foto');
      await loadPerfil();
      setFotoFile(null); setPreviewFoto(null);
      msg('success', '¡Foto de perfil actualizada!');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  /* ── Ficha ── */
  const saveFicha = async () => {
    setSaving(true);
    try {
      await api('/perfil/ficha', { method:'PUT', body: JSON.stringify(formF) });
      await loadFicha();
      setEditFicha(false);
      msg('success', 'Ficha actualizada');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  const addHijo = async () => {
    if (!formHijo.fechaNacimiento) { msg('error', 'Ingresa la fecha de nacimiento'); return; }
    setSaving(true);
    try {
      await api('/perfil/ficha/hijos', { method:'POST', body: JSON.stringify(formHijo) });
      await loadFicha();
      setFormHijo({ nombre:'', sexo:'', fechaNacimiento:'' }); setShowHijo(false);
      msg('success', 'Hijo/a agregado');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  const deleteHijo = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await api(`/perfil/ficha/hijos/${id}`, { method:'DELETE' });
      await loadFicha(); msg('success', 'Registro eliminado');
    } catch (e) { msg('error', e.message); }
  };

  /* ── Contacto emergencia ── */
  const saveEmerg = async () => {
    setSaving(true);
    try {
      await api('/perfil/contacto-emergencia', { method:'PUT', body: JSON.stringify(formE) });
      await loadContactoEmerg(); setEditEmerg(false);
      msg('success', '¡Contacto de emergencia actualizado!');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  /* ── Documentos ── */
  const uploadDocs = async (files) => {
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('documentos', f));
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/perfil/documentos`, {
        method:'POST', headers:{ Authorization:`Bearer ${getToken()}` }, body:fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Error al subir documentos (HTTP ${res.status})`);
      }
      await loadDocs(); msg('success', 'Documentos subidos correctamente');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  /* Nota: la eliminación de documentos del expediente es exclusiva de DTHS
     (ver backend/routes/perfilRoutes.js — requireHRStaff). El colaborador
     solo puede subir y consultar sus propios documentos desde su perfil. */

  /* ── Password ── */
  const changePassword = async () => {
    if (pw.nuevo !== pw.confirmar) { msg('error', 'Las contraseñas no coinciden'); return; }
    if (pw.nuevo.length < 6) { msg('error', 'Mínimo 6 caracteres'); return; }
    setSaving(true);
    try {
      await api('/perfil/cambiar-password', { method:'PUT', body: JSON.stringify({ passwordActual:pw.actual, passwordNuevo:pw.nuevo }) });
      setShowPwModal(false); setPw({ actual:'', nuevo:'', confirmar:'' });
      msg('success', '¡Contraseña actualizada!');
    } catch (e) { msg('error', e.message); } finally { setSaving(false); }
  };

  /* ─────────────────────── RENDER ─────────────────────────────── */
  if (loading) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:400,gap:16,color:C.gray600}}>
      <div style={{width:40,height:40,borderRadius:'50%',border:`3px solid ${C.gray200}`,borderTop:`3px solid ${C.blue}`,animation:'spin 1s linear infinite'}}/>
      <span style={{fontSize:14,fontWeight:500}}>Cargando perfil...</span>
    </div>
  );

  if (!perfil) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:400,gap:12,color:C.gray600}}>
      <AlertCircle size={48} color={C.red}/>
      <p>No se pudo cargar el perfil. Intenta nuevamente.</p>
    </div>
  );

  const antig = calcAntiguedad(perfil.fechaIngreso);

  const TABS = [
    { id:'personal',   label:'Información Personal', icon:User },
    { id:'ficha',      label:'Ficha del Empleado',   icon:Users },
    { id:'emergencia', label:'Contacto de Emergencia', icon:Heart },
    { id:'historial',  label:'Historial Laboral',    icon:Briefcase },
    { id:'documentos', label:'Mis Documentos',       icon:FileText },
  ];

  const initials = `${perfil.nombres?.charAt(0) || ''}${perfil.apellidos?.charAt(0) || ''}`;

  return (
    <div style={{ fontFamily:"'Segoe UI',system-ui,sans-serif", background:C.gray100, minHeight:'100vh', padding:24 }}>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes slideIn { from { transform:translateX(20px); opacity:0; } to { transform:translateX(0); opacity:1; } }
        @keyframes fadeUp { from { transform:translateY(16px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.6; } }
        input:not([disabled]):focus, textarea:not([disabled]):focus, select:not([disabled]):focus {
          outline:none !important; border-color:${C.blue} !important; box-shadow:0 0 0 3px ${C.blue}22 !important;
        }
        input[disabled], textarea[disabled] { cursor:default; }
        button { cursor:pointer; transition:opacity .15s, transform .15s; font-family:inherit; }
        button:hover:not(:disabled) { opacity:.88; transform:translateY(-1px); }
        button:disabled { opacity:.5; cursor:not-allowed; }
        ::-webkit-scrollbar { width:5px; } ::-webkit-scrollbar-thumb { background:${C.gray200}; border-radius:3px; }
      `}</style>

      {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)}/>}

      {/* ── HEADER ── */}
      <div style={{
        background:`linear-gradient(135deg, ${C.navy} 0%, #1a3a8f 40%, ${C.blue} 100%)`,
        borderRadius:16, padding:'28px 32px', marginBottom:20,
        position:'relative', overflow:'hidden',
      }}>
        {/* decorative circles */}
        <div style={{position:'absolute',top:-40,right:-40,width:200,height:200,borderRadius:'50%',background:'rgba(255,255,255,.04)'}}/>
        <div style={{position:'absolute',bottom:-60,right:80,width:140,height:140,borderRadius:'50%',background:'rgba(255,255,255,.06)'}}/>

        <div style={{display:'flex', alignItems:'center', gap:28, flexWrap:'wrap', position:'relative', zIndex:1}}>
          {/* Avatar */}
          <div style={{position:'relative', flexShrink:0}}>
            <div style={{
              width:100, height:100, borderRadius:'50%', overflow:'hidden',
              background:`linear-gradient(135deg, ${C.sky} 0%, ${C.blue} 100%)`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:32, fontWeight:700, color:C.white,
              border:'4px solid rgba(255,255,255,.3)',
              boxShadow:'0 8px 24px rgba(0,0,0,.3)',
            }}>
              {previewFoto ? (
                <img src={previewFoto} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              ) : perfil.fotoUrl ? (
                <img src={perfil.fotoUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              ) : initials}
            </div>
            <label onClick={() => fotoRef.current?.click()} style={{
              position:'absolute', bottom:2, right:2,
              width:28, height:28, borderRadius:'50%',
              background:C.gold, border:'3px solid white',
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,.2)',
            }}>
              <Camera size={12} color={C.white}/>
            </label>
            <input ref={fotoRef} type="file" accept="image/*" onChange={handleFotoSelect} style={{display:'none'}}/>
          </div>

          {/* Info */}
          <div style={{flex:1, minWidth:200, color:C.white}}>
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:4}}>
              <h1 style={{margin:0, fontSize:26, fontWeight:700, letterSpacing:-.3}}>
                {perfil.nombres} {perfil.apellidos}
              </h1>
              <span style={{
                background:'rgba(255,255,255,.2)', padding:'2px 10px', borderRadius:20,
                fontSize:11, fontWeight:600, letterSpacing:.5,
              }}>#{perfil.empleadoId}</span>
            </div>
            <p style={{margin:0, fontSize:16, opacity:.85, fontWeight:400, marginBottom:10}}>
              {perfil.cargo || 'Sin cargo asignado'}
            </p>
            <div style={{display:'flex', gap:20, flexWrap:'wrap', fontSize:13, opacity:.75}}>
              <span style={{display:'flex', alignItems:'center', gap:5}}><Building size={13}/>{perfil.departamento}</span>
              <span style={{display:'flex', alignItems:'center', gap:5}}><Calendar size={13}/>{fmtFecha(perfil.fechaIngreso)}</span>
              <span style={{display:'flex', alignItems:'center', gap:5}}><Star size={13}/>{antig.label} de antigüedad</span>
            </div>
          </div>

          {/* Stats chips */}
          <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
            {[
              { label:'Antigüedad', value: antig.label, icon:Clock },
              { label:'Cap. en Curso', value: stats.capacitacionesCurso ?? 0, icon:BookOpen },
              { label:'Cap. Pendientes', value: stats.capacitacionesPendientes ?? 0, icon:GraduationCap },
            ].map(s => {
              const Icon = s.icon;
              return (
                <div key={s.label} style={{
                  background:'rgba(255,255,255,.13)', backdropFilter:'blur(10px)',
                  padding:'12px 16px', borderRadius:12, border:'1px solid rgba(255,255,255,.2)',
                  minWidth:100, textAlign:'center', color:C.white,
                }}>
                  <Icon size={18} style={{marginBottom:4, opacity:.8}}/>
                  <div style={{fontSize:20, fontWeight:700}}>{s.value}</div>
                  <div style={{fontSize:11, opacity:.7, fontWeight:500}}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* foto upload confirm */}
        {previewFoto && (
          <div style={{
            marginTop:16, display:'flex', alignItems:'center', gap:10,
            background:'rgba(255,255,255,.15)', padding:'10px 16px', borderRadius:8,
            position:'relative', zIndex:1,
          }}>
            <Info size={14} color={C.white}/>
            <span style={{fontSize:13, color:C.white, flex:1}}>Nueva foto seleccionada. Confirma para guardar.</span>
            <button onClick={() => { setPreviewFoto(null); setFotoFile(null); }} style={{
              background:'rgba(255,255,255,.2)', border:'none', color:C.white,
              padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:600,
            }}>Cancelar</button>
            <button onClick={uploadFoto} disabled={saving} style={{
              background:C.gold, border:'none', color:C.white,
              padding:'6px 14px', borderRadius:6, fontSize:12, fontWeight:600,
              display:'flex', alignItems:'center', gap:6,
            }}>
              <Upload size={13}/>{saving ? 'Subiendo...' : 'Guardar Foto'}
            </button>
          </div>
        )}
      </div>

      {/* ── TABS ── */}
      <div style={{
        background:C.white, borderRadius:12, padding:'6px 8px',
        display:'flex', gap:4, flexWrap:'wrap', marginBottom:20,
        boxShadow:'0 1px 4px rgba(0,0,0,.06)', border:`1px solid ${C.gray200}`,
      }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex:'1 1 auto', padding:'10px 12px', border:'none', borderRadius:8,
              background: active ? C.blue : 'transparent',
              color: active ? C.white : C.gray600,
              fontWeight: active ? 600 : 500, fontSize:13,
              display:'flex', alignItems:'center', justifyContent:'center', gap:7,
              transition:'all .2s', minWidth:120,
            }}>
              <Icon size={15}/>{t.label}
            </button>
          );
        })}
      </div>

      {/* ── CONTENT ── */}
      <div style={{animation:'fadeUp .3s ease'}}>
        {tab === 'personal'   && <TabPersonal perfil={perfil} formP={formP} setFormP={setFormP} editPersonal={editPersonal} setEditP={setEditP} saving={saving} onSave={savePersonal} onShowPw={() => setShowPwModal(true)}/>}
        {tab === 'ficha'      && <TabFicha ficha={ficha} hijos={hijos} formF={formF} setFormF={setFormF} editFicha={editFicha} setEditFicha={setEditFicha} saving={saving} onSave={saveFicha} formHijo={formHijo} setFormHijo={setFormHijo} showHijoForm={showHijoForm} setShowHijo={setShowHijo} onAddHijo={addHijo} onDeleteHijo={deleteHijo}/>}
        {tab === 'emergencia' && <TabEmergencia 
          contactos={contactosEmerg} 
          empleadoId={perfil.empleadoId} 
          onContactoAgregado={loadContactoEmerg}
        />}
        {tab === 'historial'  && <TabHistorial historial={historial}/>}
        {tab === 'documentos' && <TabDocumentos docs={docs} onUpload={uploadDocs} dragOver={dragOver} setDragOver={setDragOver} fileRef={fileRef} saving={saving}/>}
      </div>

      {/* ── MODAL PASSWORD ── */}
      {showPwModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9000,padding:24}}>
          <div style={{background:C.white,borderRadius:16,width:'100%',maxWidth:420,overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
            <div style={{padding:'20px 24px',borderBottom:`1px solid ${C.gray200}`,display:'flex',justifyContent:'space-between',alignItems:'center',background:C.gray50}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <Key size={18} color={C.blue}/>
                <span style={{fontWeight:700,fontSize:15,color:C.gray800}}>Cambiar Contraseña</span>
              </div>
              <button onClick={() => setShowPwModal(false)} style={{background:'none',border:'none',color:C.gray600,padding:4}}><X size={20}/></button>
            </div>
            <div style={{padding:24, display:'flex', flexDirection:'column', gap:16}}>
              {[
                { label:'Contraseña actual', key:'actual', placeholder:'Tu contraseña actual' },
                { label:'Nueva contraseña',  key:'nuevo',  placeholder:'Mínimo 6 caracteres' },
                { label:'Confirmar nueva',   key:'confirmar', placeholder:'Repite la nueva contraseña' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{fontSize:12,fontWeight:600,color:C.gray600,textTransform:'uppercase',letterSpacing:.5,display:'block',marginBottom:6}}>{f.label}</label>
                  <input type="password" placeholder={f.placeholder} value={pw[f.key]}
                    onChange={e => setPw(p => ({...p, [f.key]:e.target.value}))}
                    style={{width:'100%',padding:'10px 12px',border:`1px solid ${C.gray200}`,borderRadius:8,fontSize:14,boxSizing:'border-box',fontFamily:'inherit'}}
                  />
                </div>
              ))}
              <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:4}}>
                <button onClick={() => setShowPwModal(false)} style={{padding:'9px 18px',background:C.gray100,border:`1px solid ${C.gray200}`,borderRadius:8,fontSize:13,fontWeight:500,color:C.gray700}}>Cancelar</button>
                <button onClick={changePassword} disabled={saving} style={{padding:'9px 18px',background:C.blue,border:'none',borderRadius:8,fontSize:13,fontWeight:600,color:C.white}}>
                  {saving ? 'Guardando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TAB: INFORMACIÓN PERSONAL
═══════════════════════════════════════════════════════════════ */
const TabPersonal = ({ perfil, formP, setFormP, editPersonal, setEditP, saving, onSave, onShowPw }) => {
  const up = (k, v) => setFormP(p => ({...p, [k]:v}));
  return (
    <div>
      {/* Datos básicos – read only */}
      <Card title="Datos Básicos" icon={User}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16}}>
          <Field label="Nombres"          value={perfil.nombres}/>
          <Field label="Apellidos"        value={perfil.apellidos}/>
          <Field label="Cédula / ID"      value={perfil.cedula}/>
          <Field label="Email Corporativo" value={perfil.email}/>
        </div>
      </Card>

      {/* Información laboral – read only */}
      <Card title="Información Laboral" icon={Briefcase}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16}}>
          <Field label="Departamento"   value={perfil.departamento}/>
          <Field label="Cargo"          value={perfil.cargo}/>
          <Field label="Fecha de Ingreso" value={fmtFecha(perfil.fechaIngreso)}/>
          <Field label="Rol en Sistema" value={perfil.rol}/>
        </div>
        {perfil.puestoDescripcion && (
          <div style={{marginTop:16}}>
            <Field label="Descripción del Puesto" value={perfil.puestoDescripcion} rows={4}/>
          </div>
        )}
      </Card>

      {/* Contacto – editable parcial */}
      <Card title="Información de Contacto" icon={Phone} action={
        !editPersonal ? (
          <button onClick={() => setEditP(true)} style={btnStyle(C.blue)}>
            <Edit size={13}/>Editar
          </button>
        ) : (
          <div style={{display:'flex',gap:8}}>
            <button onClick={() => setEditP(false)} style={btnStyle(C.gray600,'outline')}>Cancelar</button>
            <button onClick={onSave} disabled={saving} style={btnStyle(C.green)}>
              <Save size={13}/>{saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        )
      }>
        <div style={{background:'#fef9ec', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', marginBottom:16, display:'flex', gap:8, alignItems:'flex-start'}}>
          <Info size={14} color={C.gold} style={{marginTop:1, flexShrink:0}}/>
          <p style={{margin:0, fontSize:12.5, color:'#92400e', lineHeight:1.5}}>
            El Teléfono 1 y la Dirección 1 son datos institucionales administrados por RRHH y no pueden ser editados. Puedes actualizar los campos adicionales.
          </p>
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16}}>
          <Field label="Teléfono 1 (Institucional)" value={perfil.telefono}/>
          <Field label="Teléfono 2" value={formP.telefono2} editable={editPersonal} onChange={e => up('telefono2', e.target.value)} placeholder="(809) 000-0000"/>
          <Field label="Teléfono 3" value={formP.telefono3} editable={editPersonal} onChange={e => up('telefono3', e.target.value)} placeholder="(829) 000-0000"/>
          <Field label="Email Personal" value={formP.emailPersonal} editable={editPersonal} onChange={e => up('emailPersonal', e.target.value)} type="email" placeholder="tu@email.com"/>
          <Field label="Dirección 1 (Institucional)" value={perfil.direccion} fullWidth/>
          <Field label="Dirección 2" value={formP.direccion2} editable={editPersonal} onChange={e => up('direccion2', e.target.value)} placeholder="Dirección alterna" fullWidth/>
          <Field label="Dirección 3 (Residencia)" value={formP.direccion3} editable={editPersonal} onChange={e => up('direccion3', e.target.value)} placeholder="Dirección de residencia" fullWidth/>
        </div>
      </Card>

      {/* Seguridad 
      <Card title="Seguridad de la Cuenta" icon={Shield}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', background:C.gray50, borderRadius:8, border:`1px solid ${C.gray200}`}}>
          <div>
            <p style={{margin:0, fontWeight:600, fontSize:14, color:C.gray800}}>Contraseña</p>
            <p style={{margin:0, fontSize:12, color:C.gray600, marginTop:2}}>Actualiza tu contraseña periódicamente por seguridad.</p>
          </div>
          <button onClick={onShowPw} style={btnStyle(C.navy)}>
            <Key size={13}/>Cambiar
          </button>
        </div>
      </Card> */}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TAB: FICHA DEL EMPLEADO
═══════════════════════════════════════════════════════════════ */
const TabFicha = ({ ficha, hijos, formF, setFormF, editFicha, setEditFicha, saving, onSave, formHijo, setFormHijo, showHijoForm, setShowHijo, onAddHijo, onDeleteHijo }) => {
  const up = (k, v) => setFormF(p => ({...p, [k]:v}));
  const upH = (k, v) => setFormHijo(p => ({...p, [k]:v}));
  const SEXOS = ['Masculino','Femenino','No especificado'];
  const PARENTESCO = ['Coónyuge','Hijo/a','Padre','Madre','Hermano/a','Amigo/a','Familiar','Otro'];

  return (
    <div>
      <Card title="Datos Personales" icon={User} action={
        !editFicha ? (
          <button onClick={() => setEditFicha(true)} style={btnStyle(C.blue)}><Edit size={13}/>Editar</button>
        ) : (
          <div style={{display:'flex',gap:8}}>
            <button onClick={() => setEditFicha(false)} style={btnStyle(C.gray600,'outline')}>Cancelar</button>
            <button onClick={onSave} disabled={saving} style={btnStyle(C.green)}><Save size={13}/>{saving?'Guardando...':'Guardar'}</button>
          </div>
        )
      }>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16}}>
          <Field label="Fecha de Nacimiento" value={formF.fechaNacimiento} editable={editFicha}
            onChange={e => up('fechaNacimiento', e.target.value)} type="date"/>
          <div>
            <label style={labelStyle}>Sexo</label>
            {editFicha ? (
              <select value={formF.sexo || ''} onChange={e => up('sexo', e.target.value)}
                style={{width:'100%',padding:'10px 12px',border:`1px solid ${C.gray200}`,borderRadius:8,fontSize:14,background:C.white,fontFamily:'inherit'}}>
                <option value="">Seleccionar</option>
                {SEXOS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input value={formF.sexo || ''} disabled style={{...inputDisabled, width:'100%', boxSizing:'border-box'}}/>
            )}
          </div>
          <Field label="Edad" value={ficha?.fechaNacimiento ? `${calcEdad(ficha.fechaNacimiento)} años` : '—'}/>
        </div>
      </Card>

      {/* Hijos */}
      <Card title="Información de Hijos" icon={Baby} action={
        <button onClick={() => setShowHijo(true)} style={btnStyle(C.green)}><Plus size={13}/>Agregar</button>
      }>
        {hijos.length === 0 && !showHijoForm && (
          <div style={{textAlign:'center', padding:'2rem', color:C.gray600}}>
            <Baby size={40} style={{opacity:.3, margin:'0 auto 8px', display:'block'}}/>
            <p style={{fontSize:13}}>No hay hijos/as registrados</p>
          </div>
        )}

        {hijos.map((h, i) => {
          const edad = calcEdad(h.fechaNacimiento);
          return (
            <div key={h.id || i} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'12px 16px', background:C.gray50, borderRadius:8, border:`1px solid ${C.gray200}`,
              marginBottom:10, animation:'fadeUp .3s ease',
            }}>
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <div style={{width:38,height:38,borderRadius:'50%',background:`${C.sky}18`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <Baby size={17} color={C.sky}/>
                </div>
                <div>
                  <p style={{margin:0, fontWeight:600, fontSize:14, color:C.gray800}}>{h.nombre || 'Nombre no especificado'}</p>
                  <p style={{margin:0, fontSize:12, color:C.gray600}}>{h.sexo} · {fmtFecha(h.fechaNacimiento)} · <b style={{color:C.blue}}>{edad} años</b></p>
                </div>
              </div>
              <button onClick={() => onDeleteHijo(h.id)} style={{background:'none',border:`1px solid ${C.gray200}`,borderRadius:6,padding:'5px 8px',color:C.red}}>
                <Trash2 size={14}/>
              </button>
            </div>
          );
        })}

        {showHijoForm && (
          <div style={{background:C.gray50, border:`1px solid ${C.gray200}`, borderRadius:10, padding:20, marginTop:10}}>
            <p style={{margin:'0 0 14px', fontWeight:600, fontSize:14, color:C.gray800}}>Nuevo hijo/a</p>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:14}}>
              <div>
                <label style={labelStyle}>Nombre</label>
                <input value={formHijo.nombre} onChange={e => upH('nombre',e.target.value)}
                  placeholder="Nombre completo" style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>Sexo</label>
                <select value={formHijo.sexo} onChange={e => upH('sexo',e.target.value)} style={{...inputStyle}}>
                  <option value="">Seleccionar</option>
                  {SEXOS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Fecha de Nacimiento *</label>
                <input type="date" value={formHijo.fechaNacimiento} onChange={e => upH('fechaNacimiento',e.target.value)} style={inputStyle}/>
              </div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={() => setShowHijo(false)} style={btnStyle(C.gray600,'outline')}>Cancelar</button>
              <button onClick={onAddHijo} style={btnStyle(C.green)}><Plus size={13}/>Agregar</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TAB: CONTACTO DE EMERGENCIA
═══════════════════════════════════════════════════════════════ */
const TabEmergencia = ({ contactos = [], empleadoId, onContactoAgregado }) => {
  const PARENTESCO = ['Cónyuge','Hijo/a','Padre','Madre','Hermano/a','Amigo/a','Familiar','Otro'];
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState(null);
  const [form, setForm] = useState({
    nombreContacto: '', parentesco: '', telefonoContacto: ''
  });

  const up  = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const msg = (tipo, texto) => setToast({ tipo, texto });

  const agregar = async () => {
    if (!form.nombreContacto || !form.telefonoContacto) {
      msg('error', 'Nombre y teléfono son obligatorios');
      return;
    }

    setSaving(true);

    try {
      await api('/perfil/contacto-emergencia', {
        method: 'POST',
        body: JSON.stringify({
          nombreContacto: form.nombreContacto,
          parentesco: form.parentesco,
          telefonoContacto: form.telefonoContacto,
        }),
      });

    // Reset formulario
    setForm({
      nombreContacto: '',
      parentesco: '',
      telefonoContacto: '',
    });

    setShowForm(false);

    msg('success', 'Contacto guardado correctamente');

    // Recargar datos en el padre
    onContactoAgregado?.();

  } catch (e) {
    msg('error', e.message || 'Error al guardar el contacto');
  } finally {
    setSaving(false);
  }
};

  const puedeAgregar = contactos.length < 3;

  return (
    <div>
      {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)} />}

      {/* Aviso general */}
      <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:10,
        padding:'12px 16px', marginBottom:20, display:'flex', gap:10, alignItems:'flex-start' }}>
        <AlertTriangle size={16} color='#c2410c' style={{ marginTop:1, flexShrink:0 }}/>
        <p style={{ margin:0, fontSize:13, color:'#9a3412', lineHeight:1.5 }}>
          <b>Contacto de Emergencia:</b> Esta persona será contactada ante cualquier situación
          de emergencia. Puedes agregar hasta 3 contactos. Los existentes solo pueden ser
          modificados por RRHH.
        </p>
      </div>

      <Card title="Contactos de Emergencia" icon={Heart} action={
        puedeAgregar && !showForm ? (
          <button onClick={() => setShowForm(true)} style={btnStyle(C.blue)}>
            <Plus size={13}/>Agregar ({contactos.length}/3)
          </button>
        ) : null
      }>

        {/* Lista de contactos — solo lectura */}
        {contactos.length === 0 && !showForm ? (
          <div style={{ textAlign:'center', padding:'2rem', color:C.gray600 }}>
            <Heart size={40} style={{ opacity:.2, margin:'0 auto 8px', display:'block' }}/>
            <p style={{ fontSize:13 }}>No hay contactos de emergencia registrados</p>
          </div>
        ) : (
          <div style={{ display:'grid', gap:10, marginBottom: showForm ? 16 : 0 }}>
            {contactos.map((c, i) => (
              <div key={c.id || i} style={{
                background: C.gray50, border:`1px solid ${C.gray200}`,
                borderRadius:10, padding:'14px 16px',
                display:'grid',
                gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',
                gap:12, alignItems:'center',
              }}>
                <div>
                  <p style={{ margin:'0 0 2px', fontSize:11, fontWeight:600,
                    color:C.gray400, textTransform:'uppercase', letterSpacing:.5 }}>Nombre</p>
                  <p style={{ margin:0, fontSize:13, fontWeight:600,
                    color:C.gray800 }}>{c.NombreContacto || c.nombreContacto || '—'}</p>
                </div>
                <div>
                  <p style={{ margin:'0 0 2px', fontSize:11, fontWeight:600,
                    color:C.gray400, textTransform:'uppercase', letterSpacing:.5 }}>Parentesco</p>
                  <p style={{ margin:0, fontSize:13, color:C.gray800 }}>{c.Parentesco || c.parentesco || '—'}</p>
                </div>
                <div>
                  <p style={{ margin:'0 0 2px', fontSize:11, fontWeight:600,
                    color:C.gray400, textTransform:'uppercase', letterSpacing:.5 }}>Teléfono</p>
                  <p style={{ margin:0, fontSize:13, color:C.gray800 }}>{c.telefonoContacto || '—'}</p>
                </div>
                {/* Candado indicando que RRHH edita */}
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <span style={{ fontSize:11, color:C.gray400,
                    display:'flex', alignItems:'center', gap:4 }}>
                    <Lock size={11}/>Solo RRHH edita
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Formulario agregar */}
        {showForm && (
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe',
            borderRadius:10, padding:16, marginTop: contactos.length ? 4 : 0 }}>
            <p style={{ margin:'0 0 14px', fontWeight:700, fontSize:13, color:'#1e40af' }}>
              Nuevo contacto de emergencia
            </p>
            <div style={{ display:'grid',
              gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12 }}>
              <div>
                <label style={labelStyle}>Nombre Completo *</label>
                <input
                  value={form.nombreContacto}
                  onChange={e => up('nombreContacto', e.target.value)}
                  placeholder="Nombre del contacto"
                  style={{ ...inputStyle, width:'100%', boxSizing:'border-box' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Parentesco / Relación</label>
                <select
                  value={form.parentesco}
                  onChange={e => up('parentesco', e.target.value)}
                  style={{ ...inputStyle, width:'100%', boxSizing:'border-box' }}
                >
                  <option value="">Seleccionar</option>
                  {PARENTESCO.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Teléfono *</label>
                <input
                  value={form.telefonoContacto}
                  onChange={e => up('telefonoContacto', e.target.value)}
                  placeholder="(809) 000-0000"
                  type="tel"
                  style={{ ...inputStyle, width:'100%', boxSizing:'border-box' }}
                />
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:14, justifyContent:'flex-end' }}>
              <button
                onClick={() => {
                  setShowForm(false);
                  setForm({ nombreContacto:'', parentesco:'', telefonoContacto:'' });
                }}
                style={btnStyle(C.gray600, 'outline')}
              >
                Cancelar
              </button>
              <button onClick={agregar} disabled={saving} style={btnStyle(C.green)}>
                <Save size={13}/>{saving ? 'Guardando...' : 'Guardar contacto'}
              </button>
            </div>
          </div>
        )}

        {/* Límite alcanzado */}
        {!puedeAgregar && (
          <p style={{ fontSize:12, color:C.gray400, marginTop:12, textAlign:'center' }}>
            Límite de 3 contactos alcanzado. Contacta a RRHH para modificarlos.
          </p>
        )}
      </Card>

      {/* Dirección laboral — sin cambios */}
      <Card title="Dirección Laboral (Institucional)" icon={MapPin}>
        <div style={{ background:C.gray50, borderRadius:8, padding:'14px 16px',
          display:'flex', gap:12, alignItems:'flex-start' }}>
          <Lock size={15} color={C.gray400} style={{ marginTop:2 }}/>
          <div>
            <p style={{ margin:0, fontWeight:600, fontSize:14, color:C.gray800 }}>ProDominicana</p>
            <p style={{ margin:0, fontSize:13, color:C.gray600, marginTop:3 }}>
              Av. 27 de Febrero, Santo Domingo, República Dominicana
            </p>
            <p style={{ margin:'6px 0 0', fontSize:11, color:C.gray400 }}>
              Dato institucional — gestionado por RRHH
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
/* ═══════════════════════════════════════════════════════════════
   TAB: HISTORIAL LABORAL INTERNO
═══════════════════════════════════════════════════════════════ */
const ACCION_COLORS = {
  'Contratación':   { bg:'#ecfdf5', dot:'#059669', text:'#065f46', border:'#6ee7b7' },
  'Ascenso':        { bg:'#eff6ff', dot:'#2563eb', text:'#1e40af', border:'#93c5fd' },
  'Traslado':       { bg:'#fef3c7', dot:'#d97706', text:'#92400e', border:'#fcd34d' },
  'Ajuste Salarial':{ bg:'#f5f3ff', dot:'#7c3aed', text:'#4c1d95', border:'#c4b5fd' },
  'Suspensión':     { bg:'#fff7ed', dot:'#ea580c', text:'#9a3412', border:'#fdba74' },
  'Desvinculación': { bg:'#fef2f2', dot:'#dc2626', text:'#991b1b', border:'#fca5a5' },
  default:          { bg:'#f8fafc', dot:'#64748b', text:'#334155', border:'#cbd5e1' },
};

const TabHistorial = ({ historial }) => {
  const [expandedId, setExpanded] = useState(null);

  return (
    <div>
      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'12px 16px',marginBottom:20,display:'flex',gap:10,alignItems:'center'}}>
        <Info size={15} color={C.blue}/>
        <p style={{margin:0,fontSize:13,color:'#1e40af'}}>
          <b>Historial Laboral Interno:</b> Este registro es generado y gestionado por el área de RRHH. Solo lectura.
        </p>
      </div>

      {historial.length === 0 ? (
        <div style={{textAlign:'center',padding:'3rem',color:C.gray600}}>
          <Briefcase size={48} style={{opacity:.25,margin:'0 auto 12px',display:'block'}}/>
          <p style={{fontSize:14}}>No hay movimientos laborales registrados aún</p>
        </div>
      ) : (
        <div style={{position:'relative', paddingLeft:32}}>
          <div style={{position:'absolute',left:11,top:8,bottom:8,width:2,background:C.gray200,borderRadius:2}}/>

          {historial.map((item, i) => {
            const c = ACCION_COLORS[item.tipoAccion] || ACCION_COLORS.default;
            const isOpen = expandedId === item.id;
            return (
              <div key={item.id || i} style={{position:'relative',marginBottom:16,animation:`fadeUp .3s ease ${i*50}ms both`}}>
                {/* dot */}
                <div style={{
                  position:'absolute',left:-22,top:18,
                  width:14,height:14,borderRadius:'50%',
                  background:c.dot,border:'3px solid white',
                  boxShadow:`0 0 0 3px ${c.border}`,
                }}/>

                <div style={{
                  background:C.white, border:`1px solid ${c.border}`,
                  borderRadius:10, overflow:'hidden', marginLeft:8,
                  boxShadow:'0 1px 4px rgba(0,0,0,.05)',
                }}>
                  <button onClick={() => setExpanded(isOpen ? null : item.id)} style={{
                    width:'100%', background:c.bg, border:'none',
                    padding:'14px 18px', display:'flex', justifyContent:'space-between',
                    alignItems:'center', textAlign:'left', cursor:'pointer',
                  }}>
                    <div style={{display:'flex',gap:12,alignItems:'center'}}>
                      <span style={{
                        padding:'3px 10px', background:c.dot, color:'white',
                        borderRadius:20, fontSize:11, fontWeight:700,
                      }}>{item.tipoAccion}</span>
                      <div>
                        <p style={{margin:0,fontWeight:600,fontSize:14,color:c.text}}>{item.descripcion || item.tipoAccion}</p>
                        <p style={{margin:0,fontSize:12,color:C.gray600,marginTop:2}}>{fmtFecha(item.fechaAccion)}</p>
                      </div>
                    </div>
                    <ChevronRight size={16} color={C.gray400} style={{transform: isOpen?'rotate(90deg)':'none', transition:'transform .2s'}}/>
                  </button>

                  {isOpen && (
                    <div style={{padding:'16px 18px',borderTop:`1px solid ${c.border}`,animation:'fadeUp .2s ease'}}>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12}}>
                        {item.cargoAnterior && <DetailRow label="Cargo Anterior" value={item.cargoAnterior}/>}
                        {item.cargoNuevo    && <DetailRow label="Cargo Nuevo"    value={item.cargoNuevo}/>}
                        {item.depAnterior   && <DetailRow label="Depto. Anterior" value={item.depAnterior}/>}
                        {item.depNuevo      && <DetailRow label="Depto. Nuevo"   value={item.depNuevo}/>}
                        {item.motivacion    && <DetailRow label="Motivación"      value={item.motivacion} full/>}
                        {item.aprobadoPor   && <DetailRow label="Aprobado por"   value={item.aprobadoPor}/>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, full }) => (
  <div style={{gridColumn: full?'1/-1':undefined}}>
    <p style={{margin:0,fontSize:11,fontWeight:600,color:C.gray400,textTransform:'uppercase',letterSpacing:.5,marginBottom:3}}>{label}</p>
    <p style={{margin:0,fontSize:13,color:C.gray800,fontWeight:500}}>{value}</p>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   TAB: DOCUMENTOS
═══════════════════════════════════════════════════════════════ */
const DOC_TIPOS = [
  'Cédula de Identidad', 'Curriculum Vitae', 'Certificado Universitario',
  'Certificado de Capacitación', 'Diploma / Título', 'Carta de Referencia',
  'Contrato', 'Certificado Médico', 'Otro'
];

const DOC_ICONS = {
  'Cédula de Identidad': User, 'Curriculum Vitae': FileText,
  'Certificado Universitario': GraduationCap, 'Certificado de Capacitación': Award,
  'Diploma / Título': Star, default: FileText,
};

const TabDocumentos = ({ docs, onUpload, dragOver, setDragOver, fileRef, saving }) => {
  return (
    <div>
      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'12px 16px',marginBottom:20,display:'flex',gap:10,alignItems:'flex-start'}}>
        <Info size={15} color={C.blue} style={{marginTop:1,flexShrink:0}}/>
        <div style={{fontSize:13,color:'#1e40af',lineHeight:1.5}}>
          <b>Expediente Digital:</b> Los documentos que subas aquí formarán parte de tu expediente digital y podrán ser revisados por RRHH.
          {' '}Tipos sugeridos: Cédula, CV actualizado, Diplomas, Certificaciones, Cartas de referencia.
        </div>
      </div>

      {/* Drag & Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); onUpload(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        style={{
          border:`2px dashed ${dragOver ? C.blue : C.gray200}`,
          borderRadius:12, padding:'2.5rem',
          textAlign:'center', cursor:'pointer',
          background: dragOver ? '#eff6ff' : C.gray50,
          transition:'all .2s', marginBottom:24,
        }}
      >
        <Upload size={36} color={dragOver ? C.blue : C.gray400} style={{margin:'0 auto 12px',display:'block'}}/>
        <p style={{margin:0,fontWeight:600,fontSize:15,color:C.gray800,marginBottom:4}}>
          {dragOver ? 'Suelta aquí los archivos' : 'Arrastra archivos o haz clic para seleccionar'}
        </p>
        <p style={{margin:0,fontSize:12,color:C.gray600}}>PDF, DOC, DOCX, JPG, PNG — máx. 10MB por archivo</p>
        <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={e => onUpload(e.target.files)} style={{display:'none'}}/>
      </div>

      {/* Lista de documentos */}
      {docs.length === 0 ? (
        <div style={{textAlign:'center',padding:'2rem',color:C.gray600}}>
          <FileText size={40} style={{opacity:.25,margin:'0 auto 10px',display:'block'}}/>
          <p style={{fontSize:13}}>No has subido documentos todavía</p>
        </div>
      ) : (
        <div style={{display:'grid',gap:10}}>
          {docs.map((doc, i) => {
            const Icon = DOC_ICONS[doc.tipo] || DOC_ICONS.default;
            const estadoColor = doc.estado === 'Vigente' ? C.green : doc.estado === 'Por Vencer' ? C.gold : C.red;
            return (
              <div key={doc.id || i} style={{
                display:'flex',alignItems:'center',gap:14,
                background:C.white,borderRadius:10,padding:'14px 16px',
                border:`1px solid ${C.gray200}`,boxShadow:'0 1px 3px rgba(0,0,0,.04)',
                animation:`fadeUp .3s ease ${i*40}ms both`,
              }}>
                <div style={{width:42,height:42,borderRadius:8,background:`${C.blue}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <Icon size={20} color={C.blue}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                    <p style={{margin:0,fontWeight:600,fontSize:14,color:C.gray800,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{doc.nombre}</p>
                    <span style={{padding:'2px 8px',background:estadoColor+'18',color:estadoColor,borderRadius:20,fontSize:11,fontWeight:600,flexShrink:0}}>{doc.estado || 'Activo'}</span>
                  </div>
                  <p style={{margin:0,fontSize:12,color:C.gray600}}>{doc.tipo} · {fmtFecha(doc.fechaSubida)} · {doc.tamaño}</p>
                </div>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  {doc.url && (
                    <a href={doc.url} target="_blank" rel="noreferrer" style={{padding:'7px',background:C.gray100,border:`1px solid ${C.gray200}`,borderRadius:7,color:C.gray700,display:'flex',alignItems:'center'}}>
                      <Eye size={14}/>
                    </a>
                  )}
                  {doc.url && (
                    <a href={doc.url} download style={{padding:'7px',background:C.green+'18',border:`1px solid ${C.green+'44'}`,borderRadius:7,color:C.green,display:'flex',alignItems:'center'}}>
                      <Download size={14}/>
                    </a>
                  )}
                  {/* Eliminar documentos del expediente es exclusivo de DTHS.
                      El colaborador puede subir y ver, pero no borrar. */}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Shared style helpers ─────────────────────────────────── */
const labelStyle = { display:'block', fontSize:12, fontWeight:600, color:C.gray600, textTransform:'uppercase', letterSpacing:.5, marginBottom:6 };
const inputStyle = { padding:'10px 12px', border:`1px solid ${C.gray200}`, borderRadius:8, fontSize:14, background:C.white, fontFamily:'inherit', width:'100%', boxSizing:'border-box' };
const inputDisabled = { ...inputStyle, background:C.gray100, color:C.gray600 };

const btnStyle = (color, variant) => ({
  display:'flex', alignItems:'center', gap:6,
  padding:'8px 16px', borderRadius:8, border: variant === 'outline' ? `1px solid ${C.gray200}` : 'none',
  background: variant === 'outline' ? C.white : color,
  color: variant === 'outline' ? C.gray700 : C.white,
  fontSize:13, fontWeight:600, cursor:'pointer',
});

export default PerfilModule;
